import type { ReactNode } from "react";
import { Checkbox } from "./Checkbox.js";
import { Icon } from "./Icon.js";
import "./DataTable.css";

/**
 * A table for hundreds of rows.
 *
 * Three screens had hand-rolled a `<table>` before this existed, each slightly
 * differently, which is the drift a design system is for. The catalogue is 456
 * products against a dozen columns, so density, sticky headers and selection
 * are requirements rather than refinements.
 *
 * A real `<table>` rather than a div grid: a grid loses `aria-sort`, column
 * headers, and every screen-reader affordance that makes tabular data
 * navigable, and none of that is worth trading for easier CSS.
 */

export type ColumnAlign = "start" | "end" | "center";

export interface Column<T> {
  key: string;
  header: ReactNode;
  /** Any CSS width. Omit to let the column take what it needs. */
  width?: string;
  align?: ColumnAlign;
  /** Stays put while the rest scrolls sideways. At most one, by convention. */
  sticky?: boolean;
  /**
   * Renders this cell as `<th scope="row">` rather than `<td>`.
   *
   * For a matrix — fields down the side, channels across the top — the first
   * column names the row, and a screen reader announces it with every cell in
   * that row. Without it the grid is a field of values with nothing to say
   * which is which.
   */
  rowHeader?: boolean;
  sortable?: boolean;
  render: (row: T) => ReactNode;
}

export type SortState = { key: string; dir: "asc" | "desc" };

export type DataTableDensity = "compact" | "default" | "relaxed";

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  density?: DataTableDensity;
  /** Describes the table for screen readers. Required — an unnamed table is a wall. */
  caption: string;

  selectable?: boolean;
  selected?: Set<string>;
  onSelectedChange?: (next: Set<string>) => void;

  sort?: SortState;
  onSortChange?: (next: SortState) => void;

  onRowClick?: (row: T) => void;
  loading?: boolean;
  /** Rendered inside the table frame, so the header stays visible. */
  empty?: ReactNode;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  density = "default",
  caption,
  selectable = false,
  selected,
  onSelectedChange,
  sort,
  onSortChange,
  onRowClick,
  loading = false,
  empty,
}: DataTableProps<T>) {
  const allSelected = selectable && rows.length > 0 && rows.every((row) => selected?.has(rowKey(row)));

  const toggleAll = () => {
    if (!onSelectedChange) return;
    onSelectedChange(allSelected ? new Set() : new Set(rows.map(rowKey)));
  };

  const toggleOne = (key: string) => {
    if (!onSelectedChange) return;
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelectedChange(next);
  };

  const nextSort = (key: string): SortState =>
    sort?.key === key && sort.dir === "asc" ? { key, dir: "desc" } : { key, dir: "asc" };

  return (
    <div className={`ui-table ui-table--${density}`}>
      <table>
        {/* Visually hidden, but it is what a screen reader announces on entry. */}
        <caption className="ui-table__caption">{caption}</caption>

        <thead>
          <tr>
            {selectable && (
              <th className="ui-table__select" scope="col">
                <Checkbox
                  checked={allSelected}
                  onChange={toggleAll}
                  aria-label={`Select all ${rows.length} rows`}
                />
              </th>
            )}
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                style={{ width: column.width, textAlign: column.align ?? "start" }}
                className={column.sticky ? "ui-table__sticky" : undefined}
                // Only the sorted column carries a direction; the rest report
                // "none" so the sort state is unambiguous.
                aria-sort={
                  !column.sortable
                    ? undefined
                    : sort?.key === column.key
                      ? sort.dir === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                }
              >
                {column.sortable && onSortChange ? (
                  <button
                    type="button"
                    className="ui-table__sort"
                    onClick={() => onSortChange(nextSort(column.key))}
                  >
                    {column.header}
                    <Icon
                      name={sort?.key === column.key && sort.dir === "desc" ? "chevron-down" : "chevron-up"}
                      size={12}
                      className={sort?.key === column.key ? "" : "ui-table__sort-idle"}
                    />
                  </button>
                ) : (
                  column.header
                )}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {loading ? (
            /*
             * Skeleton rows at the real row height, not a spinner. A spinner
             * collapses the table and everything below it jumps when data
             * lands; the skeleton holds the layout still.
             */
            Array.from({ length: 8 }, (_, index) => (
              <tr key={`skeleton-${index}`} className="ui-table__row ui-table__row--skeleton">
                {selectable && <td />}
                {columns.map((column) => (
                  <td key={column.key} className={column.sticky ? "ui-table__sticky" : undefined}>
                    <span className="ui-table__shimmer" />
                  </td>
                ))}
              </tr>
            ))
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length + (selectable ? 1 : 0)} className="ui-table__empty">
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const key = rowKey(row);
              const isSelected = selected?.has(key) ?? false;
              return (
                <tr
                  key={key}
                  className={[
                    "ui-table__row",
                    isSelected ? "is-selected" : "",
                    onRowClick ? "is-clickable" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {selectable && (
                    <td
                      className="ui-table__select"
                      // Selecting is not navigating: without this, ticking a
                      // checkbox would also open the row.
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleOne(key)}
                        aria-label={`Select row ${key}`}
                      />
                    </td>
                  )}
                  {columns.map((column) => {
                    const Cell = column.rowHeader ? "th" : "td";
                    return (
                      <Cell
                        key={column.key}
                        scope={column.rowHeader ? "row" : undefined}
                        style={{ textAlign: column.align ?? "start" }}
                        className={column.sticky ? "ui-table__sticky" : undefined}
                      >
                        {column.render(row)}
                      </Cell>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
